import type { FC } from 'react';
import React from 'react';

import { LoadableLazy } from '@confluence/loadable';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useCreateSpaceStore } from './CreateSpaceStore.tsx';

const CreateSpaceDrawerLoader = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CreateSpaceDrawer" */ './CreateSpaceDrawer.tsx'))
			.CreateSpaceDrawer,
});

const CreateSmartSpaceDrawerLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CreateSmartSpaceDrawer" */ '../CreateSmartSpace/CreateSmartSpaceDrawer.tsx'
			)
		).CreateSmartSpaceDrawer,
});

export const ConditionalCreateSpaceDrawer: FC = () => {
	const [
		{ shouldRenderDrawer, showCreateSpaceDrawer, createSpaceButtonRef, fallbackToStandard },
		{ closeDrawer },
	] = useCreateSpaceStore();
	const { isRovoEnabled } = useSessionData();

	if (!shouldRenderDrawer) {
		return null;
	}

	// `fallbackToStandard` lets an open smart-create drawer hand off to the standard
	// flow (e.g. when AI can't produce a suggestion) without closing the drawer.
	// Keep `expValEquals` last so smart-create exposure is only tracked once the
	// other preconditions hold (and not when we've already fallen back).
	if (
		isRovoEnabled &&
		!fallbackToStandard &&
		expValEquals('cc_smart_space_create', 'isEnabled', true)
	) {
		return (
			<CreateSmartSpaceDrawerLoader
				isOpen={showCreateSpaceDrawer}
				onClose={closeDrawer}
				shouldReturnFocus={createSpaceButtonRef}
			/>
		);
	}

	return <CreateSpaceDrawerLoader />;
};
